import React from "react";
import { Phone, ArrowRight, MapPin, Smartphone, CloudHail, BadgeCheck, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal, SectionHeading } from "../components/Shared";
import { PageHeader } from "./PaintlessDentRemoval";
import { CtaBanner } from "./Home";
import Seo from "../components/Seo";
import { serviceLinks } from "../data/servicePages";
import { services, business } from "../mock";

const iconMap = { MapPin, Smartphone, CloudHail, BadgeCheck };

const Services = () => {
  return (
    <div>
      <Seo
        title="Dent Repair Services Sydney | Paintless Dent Removal | Dentcraft NSW"
        description="Dentcraft NSW dent repair services across Sydney and Western Sydney: paintless dent removal, hail damage repair, door ding repair and mobile dent repair. Free quotes."
        path="/our-services"
      />
      <PageHeader
        title="Our Dent Repair Services in Sydney"
        crumb="Our Services"
        image="https://images.unsplash.com/photo-1615906655593-ad0386982a0f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
      />

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading
            eyebrow="What We Do"
            title="Complete mobile dent repair services in Sydney"
            subtitle="From small panel dents to full car restorations — we come to your home or workplace across Western Sydney and Sydney Metro."
          />

          <div className="space-y-8">
            {services.map((s, i) => {
              const Icon = iconMap[s.icon];
              const reversed = i % 2 === 1;
              return (
                <Reveal key={s.id}>
                  <div className={`grid lg:grid-cols-2 gap-8 items-center bg-[#f7f9f2] rounded-2xl overflow-hidden ${reversed ? "lg:[&>*:first-child]:order-2" : ""}`}>
                    <div className="relative h-64 lg:h-80">
                      <img src={s.image} alt={`${s.title} \u2013 Dentcraft NSW dent repair Sydney`} className="w-full h-full object-cover" />
                    </div>
                    <div className="p-8 lg:p-12">
                      <div className="w-14 h-14 rounded-2xl bg-[#84bd00] flex items-center justify-center mb-5">
                        {Icon && <Icon size={26} className="text-white" />}
                      </div>
                      <h3 className="font-display font-extrabold text-2xl text-[#242424] mb-3">{s.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{s.long}</p>
                      <Link to="/contact" className="mt-6 inline-flex items-center gap-2 text-[#84bd00] font-semibold hover:gap-3 transition-all">
                        Enquire Now <ArrowRight size={18} />
                      </Link>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>

          {/* Dedicated service pages (internal links) */}
          <div className="mt-14">
            <h2 className="font-display font-extrabold text-2xl text-[#242424] text-center mb-8">Explore our Sydney dent repair services</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {serviceLinks.map((l) => (
                <Link
                  key={l.path}
                  to={l.path}
                  className="group flex items-center justify-between gap-2 bg-[#f7f9f2] hover:bg-[#84bd00] border border-transparent hover:border-[#84bd00] text-[#242424] hover:text-white font-semibold px-5 py-4 rounded-xl transition-colors"
                >
                  {l.label}
                  <ArrowRight size={18} className="text-[#84bd00] group-hover:text-white" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
      <section className="py-20 bg-[#242424]">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading light eyebrow="All Vehicles" title="We repair cars, trucks & motorbikes" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              "Everyday sedans & hatchbacks",
              "Prestige & luxury vehicles",
              "Classic & collector cars",
              "Trucks & commercial vehicles",
              "Motorbikes & fuel tanks",
              "Fleet & insurance repairs",
            ].map((v, i) => (
              <Reveal key={i} delay={i * 80}>
                <div className="flex items-center gap-4 bg-white/5 border border-white/10 rounded-xl p-5 hover:border-[#84bd00]/50 transition-colors">
                  <div className="shrink-0 w-9 h-9 rounded-full bg-[#84bd00] flex items-center justify-center">
                    <Check size={18} className="text-white" />
                  </div>
                  <span className="text-gray-200 font-medium">{v}</span>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="text-center mt-12">
            <a href={`tel:${business.phoneRaw}`} className="inline-flex items-center gap-2 bg-[#84bd00] hover:bg-[#75a800] text-white font-semibold px-7 py-3.5 rounded-lg transition-colors">
              <Phone size={18} /> Call {business.phone}
            </a>
          </div>
        </div>
      </section>

      <CtaBanner />
    </div>
  );
};

export default Services;
